import { useState, type ReactNode } from 'react';
import {
  getDomainPricing,
  getStorefrontSettings,
  setDomainDisplayOrder,
  setDomainPrice,
  setStorefrontDisplayLabel,
  setStorefrontFeaturedCount,
  type DomainPricingListResponse,
  type SetStorefrontFeaturedCountResponse,
  type StorefrontSettingsListResponse,
  type WireDomainPriceRow,
} from '../api/endpoints.js';
import { useApiMutation, useApiQuery } from '../api/hooks.js';
import { useCan } from '../session/can.js';
import {
  Badge,
  Banner,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  LoadingState,
  PageHead,
  TBody,
  TD,
  THead,
  TH,
  TRow,
  Table,
} from '../ui/index.js';
import type { BadgeTone } from '../ui/index.js';
import '../styles/pricing.mobile.css';

function statusTone(s: string): BadgeTone {
  if (s === 'active') return 'success';
  return 'neutral';
}

// R-GMX-PARITY — FROZEN cross-lane contracts (§0): provider label map + stable
// provider order. mailcom first, gmx second; unknown providers sort AFTER known.
// R-WEBDE/R-TONLINE (2026-09-19): appended so the operator sees the real
// provider names (not the raw ids) on the very page used to set their price.
// 2026-10-08 (operator Q96-Q99): web.de (`webde`) is removed as a provider; its
// label and slot stay so its retained (inactive) catalog rows show a name.
const PROVIDER_LABELS: Readonly<Record<string, string>> = {
  mailcom: 'mail.com',
  gmx: 'GMX',
  // D-247: GMX Mailbox (the healthy GMX parents sold as mailboxes).
  gmxbox: 'GMX',
  rambler: 'Rambler',
  webde: 'web.de',
  tonline: 'T-Online',
};
const PROVIDER_ORDER: readonly string[] = ['mailcom', 'gmx', 'gmxbox', 'rambler', 'webde', 'tonline'];
function providerRank(id: string): number {
  const i = PROVIDER_ORDER.indexOf(id);
  return i === -1 ? 99 : i;
}
function providerLabel(id: string): string {
  return PROVIDER_LABELS[id] ?? id;
}

interface ProviderGroup {
  readonly providerId: string;
  readonly label: string;
  readonly rows: readonly WireDomainPriceRow[];
}

/**
 * Group the flat catalog rows by provider and sort them for the customer-page
 * preview: provider rank ASC → displayOrder ASC → domain ASC. Lower displayOrder
 * surfaces higher on the storefront.
 */
function groupRowsByProvider(rows: readonly WireDomainPriceRow[]): readonly ProviderGroup[] {
  const byProvider = new Map<string, WireDomainPriceRow[]>();
  for (const row of rows) {
    const list = byProvider.get(row.providerId) ?? [];
    list.push(row);
    byProvider.set(row.providerId, list);
  }
  return [...byProvider.entries()]
    .sort((a, b) => providerRank(a[0]) - providerRank(b[0]) || a[0].localeCompare(b[0]))
    .map(([providerId, list]) => ({
      providerId,
      label: providerLabel(providerId),
      rows: [...list].sort(
        (x, y) =>
          (x.displayOrder ?? 1000) - (y.displayOrder ?? 1000) ||
          x.domain.localeCompare(y.domain),
      ),
    }));
}

/**
 * `/pricing` — Domain Fiyatlandırma. Lists the active `provider_pricing_catalog`
 * rows and lets a superadmin change a domain's sale price. Saving supersedes the
 * active row (writes both mil + micro) and invalidates the customer pricing cache,
 * so the storefront shows the new price on the next read. Read = `pricing:shadow_inspect`,
 * Save = `pricing:shadow_update` (superadmin).
 */
export function DomainPricingPage(): JSX.Element {
  const canView = useCan('pricing:shadow_inspect');
  const canEdit = useCan('pricing:shadow_update');
  const list = useApiQuery<DomainPricingListResponse>((o) => getDomainPricing(o), []);
  // TRACK B — per-provider featured domain count (storefront "öne çıkanlar").
  const storefront = useApiQuery<StorefrontSettingsListResponse>(
    (o) => getStorefrontSettings(o),
    [],
  );
  // Defensive: tolerate a missing/old API (during rollout the endpoint may not
  // exist yet) or a malformed payload — degrade to defaults so the whole pricing
  // page never crashes on the featured-count add-on.
  const featuredByProvider = new Map<string, number>(
    storefront.kind === 'ok' && Array.isArray(storefront.data?.rows)
      ? storefront.data.rows.map((r) => [r.providerId, r.featuredCount])
      : [],
  );
  // Admin-editable customer-facing header label per provider. Null when the API
  // omits it (rollout / no row yet) — the editor falls back to the known label.
  const labelByProvider = new Map<string, string | null>(
    storefront.kind === 'ok' && Array.isArray(storefront.data?.rows)
      ? storefront.data.rows.map((r) => [r.providerId, r.displayLabel ?? null])
      : [],
  );
  const featuredBounds =
    storefront.kind === 'ok' && storefront.data?.bounds
      ? storefront.data.bounds
      : { min: 1, max: 50 };

  return (
    <div className="pricing-page">
      <PageHead
        crumb={<>Yönetim <Icon.ChevronRight size={11} /> Domain Fiyatlandırma</>}
        title="Domain Fiyatlandırma"
        subtitle="Satıştaki her domainin birim fiyatını buradan değiştir. Kaydetme aktif fiyatı supersede eder; müşteri yüzeyi bir sonraki okumada yeni fiyatı görür."
        actions={
          <Button variant="secondary" icon={<Icon.Refresh size={14} />} onClick={() => list.reload()}>
            Yenile
          </Button>
        }
      />

      {!canView ? <ErrorState reason="capability_missing:pricing:shadow_inspect" /> : null}

      {canView ? (
        <>
          {!canEdit ? (
            <Banner tone="info">
              Fiyat değiştirme yetkisi (pricing:shadow_update) yalnızca superadmin'dedir; bu rolde Kaydet 403 döner.
            </Banner>
          ) : null}

          {list.kind === 'loading' ? <LoadingState /> : null}
          {list.kind === 'error' ? <ErrorState reason={list.reason} onRetry={list.reload} /> : null}
          {list.kind === 'ok' && list.data.rows.length === 0 ? (
            <Card>
              <EmptyState label="Aktif fiyat satırı yok." hint="provider_pricing_catalog'da status='active' satır bulunamadı." />
            </Card>
          ) : null}
          {list.kind === 'ok' && list.data.rows.length > 0 ? (
            <Card flush>
              <CardHeader title={`Aktif fiyatlar — ${list.data.rows.length}`} />
              <CardBody tight style={{ padding: 0 }}>
                {/* Phones, tablets and laptops with the sidebar (≤1279):
                    rows become stacked cards (.tbl-stack-xl, styles.css §10)
                    with data-label lines; ≥1280 keeps the desktop table. */}
                <Table stack className="tbl-stack-xl pricing-tbl">
                  <THead>
                    <TRow>
                      <TH>Domain</TH>
                      <TH>Mevcut Fiyat</TH>
                      <TH>Durum</TH>
                      <TH>Güncelleyen</TH>
                      <TH>Sıra</TH>
                      <TH>Yeni Fiyat (USD)</TH>
                    </TRow>
                  </THead>
                  <TBody>
                    {groupRowsByProvider(list.data.rows).map((group) => (
                      <ProviderRows
                        key={group.providerId}
                        group={group}
                        canEdit={canEdit}
                        featuredCount={featuredByProvider.get(group.providerId) ?? null}
                        featuredBounds={featuredBounds}
                        displayLabel={labelByProvider.get(group.providerId) ?? null}
                        onFeaturedSaved={() => storefront.reload()}
                      />
                    ))}
                  </TBody>
                </Table>
              </CardBody>
            </Card>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

/**
 * One provider block: a header row carrying the provider label (mail.com / GMX)
 * followed by that provider's domain rows in customer-page order.
 */
function ProviderRows({
  group,
  canEdit,
  featuredCount,
  featuredBounds,
  displayLabel,
  onFeaturedSaved,
}: {
  readonly group: ProviderGroup;
  readonly canEdit: boolean;
  readonly featuredCount: number | null;
  readonly featuredBounds: { readonly min: number; readonly max: number };
  readonly displayLabel: string | null;
  readonly onFeaturedSaved: () => void;
}): JSX.Element {
  return (
    <>
      <TRow className="pricing-provider-row">
        <TD colSpan={6} className="pricing-provider-cell">
          <div className="pricing-provider-head">
            <span className="pricing-provider-name">
              <strong>{group.label}</strong>{' '}
              <span className="t-mono t-xs" style={{ opacity: 0.6 }}>
                ({group.providerId})
              </span>
            </span>
            <div className="pricing-provider-editors">
              {/* key includes the loaded label so the input remounts with the
                  real value once the storefront query resolves (no silent stale). */}
              <DisplayLabelEditor
                key={`${group.providerId}:label:${displayLabel ?? 'pending'}`}
                providerId={group.providerId}
                displayLabel={displayLabel}
                fallbackLabel={group.label}
                canEdit={canEdit}
                onSaved={onFeaturedSaved}
              />
              {/* key includes the loaded count so the input remounts with the
                  real value once the storefront query resolves (no silent stale). */}
              <FeaturedCountEditor
                key={`${group.providerId}:${featuredCount ?? 'pending'}`}
                providerId={group.providerId}
                featuredCount={featuredCount}
                bounds={featuredBounds}
                canEdit={canEdit}
                onSaved={onFeaturedSaved}
              />
            </div>
          </div>
        </TD>
      </TRow>
      {group.rows.map((row) => (
        <PriceRow key={row.id} row={row} canEdit={canEdit} />
      ))}
    </>
  );
}

// Customer-facing provider header label max length (mirrors the API + DB).
const DISPLAY_LABEL_MAX = 40;

/**
 * Per-provider "Görünen ad" (customer-facing header label) editor in the
 * provider header. Loads the current display label from storefront settings and
 * PUTs a new value (trimmed, non-empty, ≤ 40 chars). Same capability gate as
 * price edit (pricing:shadow_update); reloads the storefront query on success so
 * the shown value stays in sync. Resilient to a missing label — defaults to the
 * provider's known label (the frozen PROVIDER_LABELS map / provider id).
 */
function DisplayLabelEditor({
  providerId,
  displayLabel,
  fallbackLabel,
  canEdit,
  onSaved,
}: {
  readonly providerId: string;
  readonly displayLabel: string | null;
  readonly fallbackLabel: string;
  readonly canEdit: boolean;
  readonly onSaved: () => void;
}): JSX.Element {
  // displayLabel is null until the storefront query resolves OR when the
  // provider has no saved label yet — fall back to the known label so the input
  // is pre-filled with a sensible default.
  const current = displayLabel ?? fallbackLabel;
  const [value, setValue] = useState<string>(current);
  const mutation = useApiMutation<string, SetStorefrontFeaturedCountResponse>((o, label) =>
    setStorefrontDisplayLabel(o, { providerId, displayLabel: label }),
  );

  const trimmed = value.trim();
  const valid = trimmed.length > 0 && trimmed.length <= DISPLAY_LABEL_MAX;
  const dirty = trimmed !== current.trim();

  async function save(): Promise<void> {
    if (!canEdit || !valid || !dirty) return;
    const res = await mutation.run(trimmed);
    if (res.kind === 'ok') onSaved();
  }

  return (
    <div className="pricing-editor">
      <span className="t-xs pricing-editor-label" style={{ opacity: 0.7 }}>
        Görünen ad
      </span>
      <div className="pricing-editor-ctl">
        <Input
          className="pricing-input-label"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          maxLength={DISPLAY_LABEL_MAX}
          disabled={!canEdit}
          aria-label={`${fallbackLabel} görünen ad`}
        />
        <Button
          variant="secondary"
          size="sm"
          disabled={!canEdit || !valid || !dirty}
          loading={mutation.state.kind === 'running'}
          onClick={() => void save()}
          icon={<Icon.Check size={13} />}
        >
          Kaydet
        </Button>
      </div>
      {mutation.state.kind === 'ok' ? (
        <Badge tone="success" dot>kaydedildi</Badge>
      ) : null}
      {mutation.state.kind === 'error' ? (
        <Badge tone="danger" dot className="badge-wrap"><ReasonText reason={mutation.state.reason} /></Badge>
      ) : null}
    </div>
  );
}

/**
 * TRACK B — per-provider "öne çıkan domain sayısı" editor in the provider
 * header. Loads the current count from storefront settings and PUTs a new value
 * (floor 1, max 50 — 0 is a silent no-op). Same capability gate as price edit
 * (pricing:shadow_update); reloads the storefront query on success so the shown
 * value stays in sync.
 */
function FeaturedCountEditor({
  providerId,
  featuredCount,
  bounds,
  canEdit,
  onSaved,
}: {
  readonly providerId: string;
  readonly featuredCount: number | null;
  readonly bounds: { readonly min: number; readonly max: number };
  readonly canEdit: boolean;
  readonly onSaved: () => void;
}): JSX.Element {
  // featuredCount is null only until the storefront query resolves; fall back to
  // the lower bound so the control is usable even for a provider with no row yet.
  const current = featuredCount ?? bounds.min;
  const [value, setValue] = useState<string>(String(current));
  const mutation = useApiMutation<number, SetStorefrontFeaturedCountResponse>((o, n) =>
    setStorefrontFeaturedCount(o, { providerId, featuredCount: n }),
  );

  const n = Number(value);
  const valid = Number.isInteger(n) && n >= bounds.min && n <= bounds.max;
  const dirty = value.trim() !== String(current);

  async function save(): Promise<void> {
    if (!canEdit || !valid || !dirty) return;
    const res = await mutation.run(n);
    if (res.kind === 'ok') onSaved();
  }

  return (
    <div className="pricing-editor">
      <span className="t-xs pricing-editor-label" style={{ opacity: 0.7 }}>
        Öne çıkan domain sayısı
      </span>
      <div className="pricing-editor-ctl">
        <Input
          className="pricing-input-count"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          inputMode="numeric"
          disabled={!canEdit}
          aria-label={`${providerLabel(providerId)} öne çıkan domain sayısı`}
        />
        <Button
          variant="secondary"
          size="sm"
          disabled={!canEdit || !valid || !dirty}
          loading={mutation.state.kind === 'running'}
          onClick={() => void save()}
          icon={<Icon.Check size={13} />}
        >
          Kaydet
        </Button>
      </div>
      {mutation.state.kind === 'ok' ? (
        <Badge tone="success" dot>kaydedildi</Badge>
      ) : null}
      {mutation.state.kind === 'error' ? (
        <Badge tone="danger" dot className="badge-wrap"><ReasonText reason={mutation.state.reason} /></Badge>
      ) : null}
    </div>
  );
}

/**
 * Presentation only: the desktop "Güncelleyen" column is 36ch at most. A value
 * that fits on one such line (a UUID actor id) never breaks mid-token; a longer
 * one (`pricing-updatedby-long`) may wrap anywhere so the table fits its card.
 */
const UPDATED_BY_LINE_CHARS = 36;
/**
 * Presentation only: a domain longer than this gets soft breaks after its dots
 * (it already breaks at hyphens), so the desktop Domain column does not have
 * to be as wide as its longest dotted run. Real domains are shorter and stay
 * untouched.
 */
const DOMAIN_LINE_CHARS = 24;

/**
 * Presentation only: soft line-break opportunities (<wbr>) before "@" and
 * after ".", "_", ":" and "/", so a long e-mail / id / snake_case reason wraps
 * at those points ("izzet.cantimor." / "operations@kopechka-…",
 * "display_order_" / "out_of_range") instead of mid-word. The text itself is
 * unchanged.
 */
function withSoftBreaks(value: string): ReactNode {
  const parts: ReactNode[] = [];
  let chunk = '';
  for (const ch of value) {
    if (ch === '@' && chunk) {
      parts.push(chunk, <wbr key={parts.length} />);
      chunk = '';
    }
    chunk += ch;
    if (ch === '.' || ch === '_' || ch === ':' || ch === '/') {
      parts.push(chunk, <wbr key={parts.length} />);
      chunk = '';
    }
  }
  if (chunk) parts.push(chunk);
  return <>{parts}</>;
}

/** "Güncelleyen" value: one that fits a line is left whole, so it never breaks. */
function updatedByText(value: string): ReactNode {
  return value.length > UPDATED_BY_LINE_CHARS ? withSoftBreaks(value) : value;
}

/** A save-failure reason code inside a wrapping badge (presentation only). */
function ReasonText({ reason }: { readonly reason: string }): JSX.Element {
  return <span className="pricing-reason">{withSoftBreaks(reason)}</span>;
}

function PriceRow({
  row,
  canEdit,
}: {
  readonly row: WireDomainPriceRow;
  readonly canEdit: boolean;
}): JSX.Element {
  const [value, setValue] = useState<string>(row.unitPriceUsd.toFixed(4));
  const mutation = useApiMutation<number, { readonly outcome: string }>((o, usd) =>
    setDomainPrice(o, {
      providerId: row.providerId,
      domain: row.domain,
      unitPriceUsd: usd,
      currency: row.currency,
    }),
  );

  const usd = Number(value);
  const valid = Number.isFinite(usd) && usd >= 0;
  const dirty = value.trim() !== row.unitPriceUsd.toFixed(4);

  async function save(): Promise<void> {
    if (!canEdit || !valid) return;
    await mutation.run(usd);
  }

  return (
    <TRow>
      <TD className="t-mono tbl-stack-primary pricing-domain-cell">
        {row.domain.length > DOMAIN_LINE_CHARS ? withSoftBreaks(`@${row.domain}`) : <>@{row.domain}</>}
      </TD>
      <TD className="t-mono t-num pricing-price-cell" label="Mevcut Fiyat">
        ${row.unitPriceUsd.toFixed(4)} {row.currency}
      </TD>
      <TD className="tbl-stack-status" label="Durum">
        <Badge tone={statusTone(row.status)} dot>
          {row.status}
        </Badge>
      </TD>
      <TD className="t-mono t-xs tbl-stack-full pricing-updatedby-cell" label="Güncelleyen">
        <span
          className={
            row.updatedBy && row.updatedBy.length > UPDATED_BY_LINE_CHARS
              ? 'pricing-updatedby pricing-updatedby-long'
              : 'pricing-updatedby'
          }
        >
          {row.updatedBy ? updatedByText(row.updatedBy) : '—'}
        </span>
      </TD>
      <TD className="tbl-stack-full pricing-edit-cell pricing-order-cell" label="Sıra">
        <DisplayOrderCell row={row} canEdit={canEdit} />
      </TD>
      <TD className="tbl-stack-full pricing-edit-cell pricing-newprice-cell" label="Yeni Fiyat (USD)">
        <div className="pricing-editor pricing-editor-price">
          <div className="pricing-editor-ctl">
            <Input
              className="pricing-input-price"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              inputMode="decimal"
              disabled={!canEdit}
              aria-label={`${row.domain} yeni fiyat`}
            />
            <Button
              variant="primary"
              size="sm"
              disabled={!canEdit || !valid || !dirty}
              loading={mutation.state.kind === 'running'}
              onClick={() => void save()}
              icon={<Icon.Check size={13} />}
            >
              Kaydet
            </Button>
          </div>
          {mutation.state.kind === 'ok' ? (
            <Badge tone="success" dot>kaydedildi</Badge>
          ) : null}
          {mutation.state.kind === 'error' ? (
            <Badge tone="danger" dot className="badge-wrap"><ReasonText reason={mutation.state.reason} /></Badge>
          ) : null}
        </div>
      </TD>
    </TRow>
  );
}

/**
 * Display-order editor for a single domain. Lower = higher on customer pages.
 * Calls the dedicated PUT `.../display-order` (does NOT supersede a price row).
 * Same capability gate as price edit (pricing:shadow_update).
 */
function DisplayOrderCell({
  row,
  canEdit,
}: {
  readonly row: WireDomainPriceRow;
  readonly canEdit: boolean;
}): JSX.Element {
  const [value, setValue] = useState<string>(String(row.displayOrder ?? 1000));
  const mutation = useApiMutation<number, WireDomainPriceRow>((o, order) =>
    setDomainDisplayOrder(o, {
      providerId: row.providerId,
      domain: row.domain,
      displayOrder: order,
    }),
  );

  const order = Number(value);
  const valid = Number.isInteger(order) && order >= 0;
  const dirty = value.trim() !== String(row.displayOrder ?? 1000);

  async function save(): Promise<void> {
    if (!canEdit || !valid) return;
    await mutation.run(order);
  }

  return (
    <div className="pricing-editor pricing-editor-order">
      <div className="pricing-editor-ctl">
        <Input
          className="pricing-input-order"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          inputMode="numeric"
          disabled={!canEdit}
          aria-label={`${row.domain} sıra`}
        />
        <Button
          variant="secondary"
          size="sm"
          disabled={!canEdit || !valid || !dirty}
          loading={mutation.state.kind === 'running'}
          onClick={() => void save()}
          icon={<Icon.Check size={13} />}
        >
          Sırala
        </Button>
      </div>
      {mutation.state.kind === 'ok' ? (
        <Badge tone="success" dot>ok</Badge>
      ) : null}
      {mutation.state.kind === 'error' ? (
        <Badge tone="danger" dot className="badge-wrap"><ReasonText reason={mutation.state.reason} /></Badge>
      ) : null}
    </div>
  );
}
